import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const AppContainer = styled.div`
  position: relative;
  min-height: 100vh;
  background: #f3f4f6;
  color: #1f2937;
  font-family: 'Inter', sans-serif;
`;

export const DynamicBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
    rgba(0, 180, 219, 0.1) 0%,
    transparent 50%
  );
  pointer-events: none;
  z-index: 0;
`;

export const Header = styled.header`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  padding: 1.5rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  background: rgba(243, 244, 246, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(31, 41, 55, 0.05);
  width: 100%;
  max-height: 100px;
`;

export const HeaderContent = styled.div`
  max-width: 1400px;
  width: 100%;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
`;

export const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  flex-shrink: 0;
  text-decoration: none;
`;

export const HamburgerMenu = styled.div`
  display: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

export const Nav = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-left: auto;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    padding: 1rem;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    z-index: 1000;
    transform-origin: top;
    margin-left: 0;
  }
`;

export const NavLink = styled(Link)`
  color: #1f2937;
  text-decoration: none;
  font-size: 1.1rem;
  position: relative;
  padding: 0.5rem 0;
  transition: color 0.3s ease;

  &:hover {
    color: #00B4DB;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #00B4DB, #007bff);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

export const Hero = styled.section`
  height: 100vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 2rem;
  gap: 2rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5));
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: 
      radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(0, 180, 219, 0.15) 0%, transparent 50%),
      radial-gradient(circle at calc(var(--mouse-x, 50%) + 20%) calc(var(--mouse-y, 50%) + 20%), rgba(100, 150, 250, 0.15) 0%, transparent 50%),
      radial-gradient(circle at calc(var(--mouse-x, 50%) - 20%) calc(var(--mouse-y, 50%) - 20%), rgba(170, 200, 250, 0.1) 0%, transparent 50%);
    transform: translate(-25%, -25%);
    transition: background 0.1s ease;
    z-index: 2;
  }

  @keyframes gradientMove {
    0% {
      transform: rotate(0deg) scale(1.5);
    }
    50% {
      transform: rotate(180deg) scale(1.5);
    }
    100% {
      transform: rotate(360deg) scale(1.5);
    }
  }

  @keyframes patternMove {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 50px 50px;
    }
  }
`;

export const HeroContent = styled(motion.div)`
  max-width: 1000px;
  text-align: center;
  position: relative;
  z-index: 2;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
`;

export const Title = styled(motion.h1)`
  font-size: 4.5rem;
  padding-top: 120px;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;
  position: relative;
  text-align: center;
  transform: perspective(1000px) rotateX(var(--mouse-y-rotate, 0deg)) rotateY(var(--mouse-x-rotate, 0deg));
  transition: transform 0.1s ease;
  z-index: 3;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00B4DB, transparent);
  }

      @media (max-width: 768px) {
      font-size: 3.5rem;
      margin-top: 0px;
      padding-top: 0px;
  }

`;

export const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #f3f4f6;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  transform: perspective(1000px) rotateX(var(--mouse-y-rotate, 0deg)) rotateY(var(--mouse-x-rotate, 0deg));
  transition: transform 0.1s ease;
  z-index: 3;
  max-width: 800px;
`;

export const CTAButton = styled(motion.button)`
  background: linear-gradient(45deg, #00B4DB, #007bff);
  color: white;
  border: none;
  padding: 1.2rem 3.4rem;
  font-size: 1.5rem;
  border-radius: 50px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 15px rgba(0, 180, 219, 0.4);
  transform: perspective(1000px) rotateX(var(--mouse-y-rotate, 0deg)) rotateY(var(--mouse-x-rotate, 0deg));
  transition: transform 0.1s ease;
  z-index: 3;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: 0.5s;
  }

  &:hover {
    transform: perspective(1000px) rotateX(var(--mouse-y-rotate, 0deg)) rotateY(var(--mouse-x-rotate, 0deg)) translateY(-2px);
    box-shadow: 0 0 25px rgba(0, 180, 219, 0.6);
    
    &::before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    height: 50px;
    padding: 0 2rem;
    font-size: 1.2rem;
}

`;

export const Services = styled.section`
  padding: 8rem 2rem;
  background: #f3f4f6;
  position: relative;
  text-align: center;
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto 4rem auto;
`;

export const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  text-decoration: none;

  h3 {
    font-size: 1.6rem;
    margin-bottom: 0.8rem;
    color:rgb(80, 80, 80);
    transition: color 0.3s ease-in-out;
    border-bottom: none;
    box-shadow: none;
  }

  p {
    font-size: 1rem;
    color: #4b5563;
    line-height: 1.6;
    transition: color 0.3s ease-in-out;
  }

  svg {
    transition: color 0.3s ease-in-out;
  }

  &:hover {
    background: linear-gradient(45deg, #00B4DB, #007bff);
    border-color: rgba(0, 180, 219, 0.5);
    box-shadow: 0 8px 30px rgba(0, 180, 219, 0.4);
    transform: translateY(-10px) scale(1.02);

    h3,
    p,
    svg {
      color: white !important;
    }
  }
`;

export const SectionTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #1f2937;
  text-align: center;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #1f2937;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem auto;
`;

export const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  background: #f3f4f6;
  text-align: center;
`;

export const TestimonialCarousel = styled(motion.div)`
  position: relative;
  max-width: 700px;
  margin: 2rem auto;
  overflow: hidden;
  height: 300px;
      
  @media (max-width: 768px) {
      height: 500px
  }

`;

export const TestimonialSlide = styled(motion.div)`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transform: translateX(100%);
  transition: all 0.5s ease;

  &.active {
    opacity: 1;
    transform: translateX(0);
  }

  &.prev {
    transform: translateX(-100%);
  }
`;

export const TestimonialDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 2rem;
`;

export const TestimonialDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#00B4DB' : '#ccd7e0'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    background: ${props => props.active ? '#00B4DB' : '#aebac8'};
    transform: scale(1.2);
  }
`;

export const TestimonialCard = styled(motion.div)`
  background: #ffffff;
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(31, 41, 55, 0.08);
  text-align: left;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  p {
    font-size: 1.1rem;
    color: #1f2937;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }

  h4 {
    font-size: 1.2rem;
    color: #00B4DB;
    font-weight: 600;
    margin: 0;
  }

  span {
    color: #4b5563;
    font-size: 0.9rem;
  }
`;

export const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
      @media (max-width: 768px) {
      padding: 0px;
  }

`;

export const ContactForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 700px;
  width: 100%;
  margin-left: auto;
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(31, 41, 55, 0.15);
  padding: 0.8rem;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      padding-top: 2rem;
      padding-bottom: 2rem;
      width: 100%;
      margin-bottom: 6rem
  }
`;

export const ContactInput = styled.input`
  flex: 1;
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  color: #1f2937;
  background: #e5e7eb;

  &:focus {
    outline: none;
    background: #d1d5db;
  }
`;

export const SubmitButton = styled(motion.button)`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 0.8rem 1rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

export const ContactContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
  margin: 0 auto;

  @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      align-items: center;
  }

`;

export const ContactTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin: 0;
  text-align: left;
  flex-shrink: 0;

    @media (max-width: 768px) {
      margin-top: 6rem;
      margin-bottom: 2rem;
  }

`;

export const Footer = styled.footer`
  background: #1f2937;
  color: #f3f4f6;
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

export const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 3rem;
  max-width: 1200px;
  width: 100%;
  justify-items: center;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

export const FooterSectionTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #f3f4f6;
  text-align: center;
`;

export const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

export const FooterLogoText = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

export const FooterContact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  p {
    color: #f3f4f6;
    font-size: 0.9rem;
  }

  a {
    color: #f3f4f6;
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      color: #00B4DB;
    }
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;

  a {
    color: #f3f4f6;
    font-size: 1.4rem;
    transition: color 0.3s ease;

    &:hover {
      color: #00B4DB;
    }
  }
`;

export const FooterNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

export const FooterNavLink = styled(Link)`
  color: #f3f4f6;
  text-decoration: none;
  font-size: 0.9rem;
  position: relative;
  transition: color 0.3s ease;

  &:hover {
    color: #00B4DB;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 50%;
    transform: translateX(-50%) scaleX(0);
    transform-origin: center;
    width: 100%;
    height: 1px;
    background: #00B4DB;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(-50%) scaleX(1);
  }
`;

export const FooterCopyright = styled.p`
  font-size: 0.8rem;
  margin-top: 4rem;
  color: #9ca3af;
  width: 100%;
  text-align: center;
`;

export const VideoBackground = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  transform: translateX(-50%) translateY(-50%);
  /* filter: brightness(0.6); */
`;

export const SubmitMessage = styled.div`
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
  font-size: 0.9rem;
  background: ${props => props.success ? 'rgba(0, 200, 83, 0.1)' : 'rgba(255, 0, 0, 0.1)'};
  color: ${props => props.success ? '#00c853' : '#ff0000'};
  border: 1px solid ${props => props.success ? '#00c853' : '#ff0000'};
`;